/**
 * トークン管理のためのユーティリティクラス
 * - localStorageとsessionStorageの両方を使用してトークンを管理
 * - ストレージアクセスが制限されている場合の対応も含む
 * - トークンの保存、取得、クリアなどの機能を提供
 */

import { logger } from './logger';
import { clearAllAdmanStorageForLogout } from '../components/adman/admanStorage';

// 一貫したキー名の使用（キー名が変わらないよう定数化）
const TOKEN_KEY = 'tasky_auth_token';
const REFRESH_TOKEN_KEY = 'tasky_auth_refresh_token';
const USER_KEY = 'tasky_user_data';

// ストレージアクセスが可能かチェック
const isStorageAvailable = (storageType: 'localStorage' | 'sessionStorage'): boolean => {
  try {
    const storage = window[storageType];
    const testKey = '__storage_test__';
    storage.setItem(testKey, '1');
    storage.removeItem(testKey);
    return true;
  } catch (e) {
    console.warn(`tokenManager: ${storageType}にアクセスできません - ${e}`);
    return false;
  }
};

// メモリ内にフォールバック用のストレージを持つ
const memoryStorage: Record<string, string> = {};

// 利用可能なストレージをチェック
const canUseLocalStorage = isStorageAvailable('localStorage');
const canUseSessionStorage = isStorageAvailable('sessionStorage');


// 既存データ移行（旧キーから新キーへ）
const migrateOldData = () => {
  
  // 旧キー名の定義
  const OLD_TOKEN_KEY = 'token';
  const OLD_REFRESH_TOKEN_KEY = 'refresh_token';
  const OLD_USER_KEY = 'user_data';

  // Richka→Tasky ブランド移行用の旧キー名
  const RICHIKA_TOKEN_KEY = 'richika_auth_token';
  const RICHIKA_REFRESH_TOKEN_KEY = 'richika_auth_refresh_token';
  const RICHIKA_USER_KEY = 'richika_user_data';

  try {
    // localStorage内の旧キーを確認・移行
    if (canUseLocalStorage) {
      try {
        // richika_* キーからの移行（Richka→Taskyブランド変更）
        const richikaToken = localStorage.getItem(RICHIKA_TOKEN_KEY);
        if (richikaToken && !localStorage.getItem(TOKEN_KEY)) {
          localStorage.setItem(TOKEN_KEY, richikaToken);
        }
        if (richikaToken) localStorage.removeItem(RICHIKA_TOKEN_KEY);

        const richikaRefreshToken = localStorage.getItem(RICHIKA_REFRESH_TOKEN_KEY);
        if (richikaRefreshToken && !localStorage.getItem(REFRESH_TOKEN_KEY)) {
          localStorage.setItem(REFRESH_TOKEN_KEY, richikaRefreshToken);
        }
        if (richikaRefreshToken) localStorage.removeItem(RICHIKA_REFRESH_TOKEN_KEY);

        const richikaUserData = localStorage.getItem(RICHIKA_USER_KEY);
        if (richikaUserData && !localStorage.getItem(USER_KEY)) {
          localStorage.setItem(USER_KEY, richikaUserData);
        }
        if (richikaUserData) localStorage.removeItem(RICHIKA_USER_KEY);

        // アクセストークンの移行（旧 'token' キー）
        const oldToken = localStorage.getItem(OLD_TOKEN_KEY);
        if (oldToken && !localStorage.getItem(TOKEN_KEY)) {
          localStorage.setItem(TOKEN_KEY, oldToken);
        }
        if (oldToken) localStorage.removeItem(OLD_TOKEN_KEY);

        // リフレッシュトークンの移行
        const oldRefreshToken = localStorage.getItem(OLD_REFRESH_TOKEN_KEY);
        if (oldRefreshToken && !localStorage.getItem(REFRESH_TOKEN_KEY)) {
          localStorage.setItem(REFRESH_TOKEN_KEY, oldRefreshToken);
        }
        if (oldRefreshToken) localStorage.removeItem(OLD_REFRESH_TOKEN_KEY);

        // ユーザーデータの移行
        const oldUserData = localStorage.getItem(OLD_USER_KEY);
        if (oldUserData && !localStorage.getItem(USER_KEY)) {
          localStorage.setItem(USER_KEY, oldUserData);
        }
        if (oldUserData) localStorage.removeItem(OLD_USER_KEY);
      } catch (e) {
        console.error('tokenManager: localStorage内の旧データ移行中にエラーが発生しました', e);
      }
    }
    
    // sessionStorage内の旧キーを確認・移行
    if (canUseSessionStorage) {
      try {
        // richika_* キーからの移行（Richka→Taskyブランド変更）
        const richikaToken = sessionStorage.getItem(RICHIKA_TOKEN_KEY);
        if (richikaToken && !sessionStorage.getItem(TOKEN_KEY)) {
          sessionStorage.setItem(TOKEN_KEY, richikaToken);
        }
        if (richikaToken) sessionStorage.removeItem(RICHIKA_TOKEN_KEY);

        const richikaRefreshToken = sessionStorage.getItem(RICHIKA_REFRESH_TOKEN_KEY);
        if (richikaRefreshToken && !sessionStorage.getItem(REFRESH_TOKEN_KEY)) {
          sessionStorage.setItem(REFRESH_TOKEN_KEY, richikaRefreshToken);
        }
        if (richikaRefreshToken) sessionStorage.removeItem(RICHIKA_REFRESH_TOKEN_KEY);

        const richikaUserData = sessionStorage.getItem(RICHIKA_USER_KEY);
        if (richikaUserData && !sessionStorage.getItem(USER_KEY)) {
          sessionStorage.setItem(USER_KEY, richikaUserData);
        }
        if (richikaUserData) sessionStorage.removeItem(RICHIKA_USER_KEY);

        // アクセストークンの移行（旧 'token' キー）
        const oldToken = sessionStorage.getItem(OLD_TOKEN_KEY);
        if (oldToken && !sessionStorage.getItem(TOKEN_KEY)) {
          sessionStorage.setItem(TOKEN_KEY, oldToken);
        }
        if (oldToken) sessionStorage.removeItem(OLD_TOKEN_KEY);

        // リフレッシュトークンの移行
        const oldRefreshToken = sessionStorage.getItem(OLD_REFRESH_TOKEN_KEY);
        if (oldRefreshToken && !sessionStorage.getItem(REFRESH_TOKEN_KEY)) {
          sessionStorage.setItem(REFRESH_TOKEN_KEY, oldRefreshToken);
        }
        if (oldRefreshToken) sessionStorage.removeItem(OLD_REFRESH_TOKEN_KEY);

        // ユーザーデータの移行
        const oldUserData = sessionStorage.getItem(OLD_USER_KEY);
        if (oldUserData && !sessionStorage.getItem(USER_KEY)) {
          sessionStorage.setItem(USER_KEY, oldUserData);
        }
        if (oldUserData) sessionStorage.removeItem(OLD_USER_KEY);
      } catch (e) {
        console.error('tokenManager: sessionStorage内の旧データ移行中にエラーが発生しました', e);
      }
    }
    
  } catch (error) {
    console.error('tokenManager: データ移行中にエラーが発生しました', error);
  }
};

// 初期化時に既存データを移行
migrateOldData();

export const tokenManager = {
  /**
   * アクセストークンを保存する
   * @param token アクセストークン
   */
  saveToken: (token: string) => {
    if (!token) return;
    
    try {
      // 保存前の状態をログ
      let localStorage_status = 'none';
      let sessionStorage_status = 'none';
      
      try {
        if (canUseLocalStorage) {
          localStorage_status = localStorage.getItem(TOKEN_KEY) ? 'exists' : 'none';
        }
        if (canUseSessionStorage) {
          sessionStorage_status = sessionStorage.getItem(TOKEN_KEY) ? 'exists' : 'none';
        }
      } catch (e) {
        console.warn('tokenManager: ストレージ状態チェック中にエラーが発生しました', e);
      }
      
      // トークンを各ストレージに保存（利用可能な場合のみ）
      let localSuccess = false;
      let sessionSuccess = false;
      
      if (canUseLocalStorage) {
        try {
          localStorage.setItem(TOKEN_KEY, token);
          const localStoredToken = localStorage.getItem(TOKEN_KEY);
          localSuccess = !!localStoredToken;
        } catch (e) {
          console.error('tokenManager: localStorageへの保存に失敗しました', e);
        }
      }
      
      if (canUseSessionStorage) {
        try {
          sessionStorage.setItem(TOKEN_KEY, token);
          const sessionStoredToken = sessionStorage.getItem(TOKEN_KEY);
          sessionSuccess = !!sessionStoredToken;
        } catch (e) {
          console.error('tokenManager: sessionStorageへの保存に失敗しました', e);
        }
      }
      
      // メモリストレージにも常に保存
      memoryStorage[TOKEN_KEY] = token;
      
    } catch (error) {
      console.error('tokenManager: アクセストークン保存中にエラーが発生しました', error);
      // エラーが発生してもメモリには保存しておく
      memoryStorage[TOKEN_KEY] = token;
    }
  },

  /**
   * リフレッシュトークンを保存する
   * @param refreshToken リフレッシュトークン
   */
  saveRefreshToken: (refreshToken: string) => {
    if (!refreshToken) return;
    
    try {
      // 保存前の状態をログ
      let localStorage_status = 'none';
      let sessionStorage_status = 'none';
      
      try {
        if (canUseLocalStorage) {
          localStorage_status = localStorage.getItem(REFRESH_TOKEN_KEY) ? 'exists' : 'none';
        }
        if (canUseSessionStorage) {
          sessionStorage_status = sessionStorage.getItem(REFRESH_TOKEN_KEY) ? 'exists' : 'none';
        }
      } catch (e) {
        console.warn('tokenManager: ストレージ状態チェック中にエラーが発生しました', e);
      }
      
      // リフレッシュトークンを各ストレージに保存（利用可能な場合のみ）
      let localSuccess = false;
      let sessionSuccess = false;
      
      if (canUseLocalStorage) {
        try {
          localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
          const localStoredToken = localStorage.getItem(REFRESH_TOKEN_KEY);
          localSuccess = !!localStoredToken;
        } catch (e) {
          console.error('tokenManager: localStorageへの保存に失敗しました', e);
        }
      }
      
      if (canUseSessionStorage) {
        try {
          sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
          const sessionStoredToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
          sessionSuccess = !!sessionStoredToken;
        } catch (e) {
          console.error('tokenManager: sessionStorageへの保存に失敗しました', e);
        }
      }
      
      // メモリストレージにも常に保存
      memoryStorage[REFRESH_TOKEN_KEY] = refreshToken;
    } catch (error) {
      console.error('tokenManager: リフレッシュトークン保存中にエラーが発生しました', error);
      // エラーが発生してもメモリには保存しておく
      memoryStorage[REFRESH_TOKEN_KEY] = refreshToken;
    }
  },

  /**
   * ユーザーデータを保存する
   * @param userData ユーザーデータ
   */
  saveUserData: (userData: any) => {
    if (!userData) return;
    
    try {
      const userString = JSON.stringify(userData);
      
      // ユーザーデータを各ストレージに保存（利用可能な場合のみ）
      let localSuccess = false;
      let sessionSuccess = false;
      
      if (canUseLocalStorage) {
        try {
          localStorage.setItem(USER_KEY, userString);
          const storedData = localStorage.getItem(USER_KEY);
          localSuccess = !!storedData;
        } catch (e) {
          console.error('tokenManager: ユーザーデータのlocalStorageへの保存に失敗しました', e);
        }
      }
      
      if (canUseSessionStorage) {
        try {
          sessionStorage.setItem(USER_KEY, userString);
          const storedData = sessionStorage.getItem(USER_KEY);
          sessionSuccess = !!storedData;
        } catch (e) {
          console.error('tokenManager: ユーザーデータのsessionStorageへの保存に失敗しました', e);
        }
      }
      
      // メモリストレージにも常に保存
      memoryStorage[USER_KEY] = userString;
      
    } catch (error) {
      console.error('tokenManager: ユーザーデータ保存中にエラーが発生しました', error);
    }
  },

  /**
   * アクセストークンを取得する
   * @returns アクセストークン、なければnull
   */
  getToken: (): string | null => {
    try {
      // 各ストレージからトークンを取得
      let localToken = null;
      let sessionToken = null;
      
      // ローカルストレージから取得
      if (canUseLocalStorage) {
        try {
          localToken = localStorage.getItem(TOKEN_KEY);
        } catch (e) {
          console.warn('tokenManager: localStorageからのトークン取得に失敗しました', e);
        }
      }
      
      // セッションストレージから取得
      if (canUseSessionStorage) {
        try {
          sessionToken = sessionStorage.getItem(TOKEN_KEY);
        } catch (e) {
          console.warn('tokenManager: sessionStorageからのトークン取得に失敗しました', e);
        }
      }
      
      // メモリストレージから取得
      const memoryToken = memoryStorage[TOKEN_KEY];
      
      // いずれかのストレージからトークンを取得（優先順位：local > session > memory）
      const token = localToken || sessionToken || memoryToken;
      
      if (token) {
        // 同期を確保（利用可能なストレージにのみ保存）
        try {
          if (canUseLocalStorage && !localToken) {
            try {
              localStorage.setItem(TOKEN_KEY, token);
            } catch (e) {
              console.warn('tokenManager: localStorageへのトークン同期に失敗しました', e);
            }
          }
          
          if (canUseSessionStorage && !sessionToken) {
            try {
              sessionStorage.setItem(TOKEN_KEY, token);
            } catch (e) {
              console.warn('tokenManager: sessionStorageへのトークン同期に失敗しました', e);
            }
          }
          
          // メモリストレージにも常に保存
          memoryStorage[TOKEN_KEY] = token;
          
          // 同期後の状態を確認
        } catch (syncError) {
          console.error('tokenManager: トークン同期中にエラーが発生しました', syncError);
        }
        
        return token;
      } else {
        return null;
      }
    } catch (error) {
      console.error('tokenManager: トークン取得中にエラーが発生しました', error);
      // メモリストレージからのみ取得を試みる
      return memoryStorage[TOKEN_KEY] || null;
    }
  },

  /**
   * リフレッシュトークンを取得する
   * @returns リフレッシュトークン、なければnull
   */
  getRefreshToken: (): string | null => {
    try {
      // 各ストレージからリフレッシュトークンを取得
      let localRefreshToken = null;
      let sessionRefreshToken = null;
      
      // ローカルストレージから取得
      if (canUseLocalStorage) {
        try {
          localRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        } catch (e) {
          console.warn('tokenManager: localStorageからのリフレッシュトークン取得に失敗しました', e);
        }
      }
      
      // セッションストレージから取得
      if (canUseSessionStorage) {
        try {
          sessionRefreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY);
        } catch (e) {
          console.warn('tokenManager: sessionStorageからのリフレッシュトークン取得に失敗しました', e);
        }
      }
      
      // メモリストレージから取得
      const memoryRefreshToken = memoryStorage[REFRESH_TOKEN_KEY];
      
      if (process.env.NODE_ENV !== 'production') {
        logger.log('tokenManager: getRefreshToken - ストレージの状態', {
          hasLocalToken: !!localRefreshToken,
          hasSessionToken: !!sessionRefreshToken,
          hasMemoryToken: !!memoryRefreshToken,
          localTokenLength: localRefreshToken?.length,
          sessionTokenLength: sessionRefreshToken?.length,
          memoryTokenLength: memoryRefreshToken?.length
        });
      }
      
      // いずれかのストレージからリフレッシュトークンを取得（優先順位：local > session > memory）
      const refreshToken = localRefreshToken || sessionRefreshToken || memoryRefreshToken;
      
      if (refreshToken) {
        // 同期を確保（利用可能なストレージにのみ保存）
        try {
          if (canUseLocalStorage && !localRefreshToken) {
            try {
              localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
            } catch (e) {
              console.warn('tokenManager: localStorageへのリフレッシュトークン同期に失敗しました', e);
            }
          }
          
          if (canUseSessionStorage && !sessionRefreshToken) {
            try {
              sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
            } catch (e) {
              console.warn('tokenManager: sessionStorageへのリフレッシュトークン同期に失敗しました', e);
            }
          }
          
          // メモリストレージにも常に保存
          memoryStorage[REFRESH_TOKEN_KEY] = refreshToken;
          
          // 同期後の状態を確認
          if (process.env.NODE_ENV !== 'production') {
            logger.log('tokenManager: リフレッシュトークン同期完了', {
              inMemory: !!memoryStorage[REFRESH_TOKEN_KEY]
            });
          }
        } catch (syncError) {
          console.error('tokenManager: リフレッシュトークン同期中にエラーが発生しました', syncError);
        }
        
        return refreshToken;
      } else {
        if (process.env.NODE_ENV !== 'production') {
          logger.log('tokenManager: どのストレージにもリフレッシュトークンがありません');
        }
        return null;
      }
    } catch (error) {
      console.error('tokenManager: リフレッシュトークン取得中にエラーが発生しました', error);
      // メモリストレージからのみ取得を試みる
      return memoryStorage[REFRESH_TOKEN_KEY] || null;
    }
  },

  /**
   * ユーザーデータを取得する
   * @returns ユーザーデータ、なければnull
   */
  getUserData: () => {
    try {
      // 各ストレージからユーザーデータを取得
      let userString = null;
      
      // まずローカルストレージから取得
      if (canUseLocalStorage) {
        try {
          userString = localStorage.getItem(USER_KEY);
        } catch (e) {
          console.warn('tokenManager: localStorageからのユーザーデータ取得に失敗しました', e);
        }
      }
      
      // 次にセッションストレージから取得
      if (!userString && canUseSessionStorage) {
        try {
          userString = sessionStorage.getItem(USER_KEY);
        } catch (e) {
          console.warn('tokenManager: sessionStorageからのユーザーデータ取得に失敗しました', e);
        }
      }
      
      // 最後にメモリストレージから取得
      if (!userString) {
        userString = memoryStorage[USER_KEY];
      }
      
      if (!userString) {
        if (process.env.NODE_ENV !== 'production') {
          logger.log('tokenManager: ユーザーデータが見つかりません');
        }
        return null;
      }
      
      try {
        const userData = JSON.parse(userString);
        if (process.env.NODE_ENV !== 'production') {
          logger.log('tokenManager: ユーザーデータを取得しました');
        }
        return userData;
      } catch (parseError) {
        console.error('tokenManager: ユーザーデータの解析に失敗しました', parseError);
        return null;
      }
    } catch (error) {
      console.error('tokenManager: ユーザーデータ取得中にエラーが発生しました', error);
      return null;
    }
  },

  /**
   * トークンをクリアする
   * @param clearRefreshToken リフレッシュトークンも削除するかどうか
   */
  clearTokens: (clearRefreshToken = false) => {
    try {
      // アクセストークンをクリア
      if (canUseLocalStorage) {
        try {
          localStorage.removeItem(TOKEN_KEY);
        } catch (e) {
          console.warn('tokenManager: localStorageからのトークン削除に失敗しました', e);
        }
      }
      
      if (canUseSessionStorage) {
        try {
          sessionStorage.removeItem(TOKEN_KEY);
        } catch (e) {
          console.warn('tokenManager: sessionStorageからのトークン削除に失敗しました', e);
        }
      }
      
      // メモリストレージからも削除
      delete memoryStorage[TOKEN_KEY];
      
      if (process.env.NODE_ENV !== 'production') {
        logger.log('tokenManager: アクセストークンを削除しました');
      }
      
      // リフレッシュトークンも削除する場合
      if (clearRefreshToken) {
        if (canUseLocalStorage) {
          try {
            localStorage.removeItem(REFRESH_TOKEN_KEY);
          } catch (e) {
            console.warn('tokenManager: localStorageからのリフレッシュトークン削除に失敗しました', e);
          }
        }
        
        if (canUseSessionStorage) {
          try {
            sessionStorage.removeItem(REFRESH_TOKEN_KEY);
          } catch (e) {
            console.warn('tokenManager: sessionStorageからのリフレッシュトークン削除に失敗しました', e);
          }
        }
        
        // メモリストレージからも削除
        delete memoryStorage[REFRESH_TOKEN_KEY];
        
        if (process.env.NODE_ENV !== 'production') {
          logger.log('tokenManager: リフレッシュトークンも削除しました');
        }
      }
    } catch (error) {
      console.error('tokenManager: トークン削除中にエラーが発生しました', error);
    }
  },

  /**
   * すべての認証データをクリアする
   */
  clearAllAuthData: () => {
    try {
      // すべてのストレージからすべての認証データを削除
      const keys = [TOKEN_KEY, REFRESH_TOKEN_KEY, USER_KEY];
      
      // localStorageから削除
      if (canUseLocalStorage) {
        keys.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            console.warn(`tokenManager: localStorageからの${key}削除に失敗しました`, e);
          }
        });
      }
      
      // sessionStorageから削除
      if (canUseSessionStorage) {
        keys.forEach(key => {
          try {
            sessionStorage.removeItem(key);
          } catch (e) {
            console.warn(`tokenManager: sessionStorageからの${key}削除に失敗しました`, e);
          }
        });
      }
      
      // 広告自動化マン（Adman）の会話履歴等も削除する。
      // adman.<userId>.<name> で名前空間化されているが、ログアウト時はどのユーザーか
      // 気にせず adman. 接頭辞のキーを丸ごと削除する（同一タブでのアカウント切替時に
      // 前の利用者の会話が次の利用者へ漏れるのを防ぐ。FIXES.md F7）。
      clearAllAdmanStorageForLogout();

      // メモリストレージからも削除
      keys.forEach(key => {
        delete memoryStorage[key];
      });
      
      if (process.env.NODE_ENV !== 'production') {
        logger.log('tokenManager: すべての認証データを削除しました');
      }
    } catch (error) {
      console.error('tokenManager: 認証データ削除中にエラーが発生しました', error);
    }
  },
  
  /**
   * 有効なトークンがあるか確認する
   * @returns 有効なトークンがあればtrue
   */
  hasValidToken: (): boolean => {
    const token = tokenManager.getToken();
    const isValid = !!token && token.length > 20; // 有効なトークンの最小長をチェック
    if (process.env.NODE_ENV !== 'production') {
      logger.log('tokenManager: Token validity check', { isValid, tokenExists: !!token });
    }
    return isValid;
  },
  
  /**
   * リフレッシュトークンがあるか確認する
   * @returns リフレッシュトークンがあればtrue
   */
  hasRefreshToken: (): boolean => {
    const refreshToken = tokenManager.getRefreshToken();
    return !!refreshToken && refreshToken.length > 20;
  }
};

export default tokenManager;
